import React from 'react';
import { useSelector } from 'react-redux';
import { isStudyFinished, isStudyRunning, ListedStudy } from 'helper/studies';
import { Col, Row, Stack } from 'react-bootstrap';
import ActionButton from 'components/ActionButton';
import { useGetUserQuery } from 'services/api/users';
import { useGetCompanyQuery } from 'services/api/companies';
import { useReexecuteMutation } from 'services/api/products/flux';
import { useHandleRequestErrors } from 'utils/hooks';
import { ResponseError } from 'services/api/base';
import { useGetDownloadLinkQuery } from 'services/api/studies';
import { Link } from 'react-router-dom';
import StatusTag from 'components/Flux/Personalized/Row/StatusTag';
import Snackbar from 'components/DesignSystem/Snackbar';
import { selectUserId, selectUserIsAdmin } from 'redux/reducers/authReducer';
import useStudyRowActions from './useStudyRowActions';

export default function StudyRow({ study }: { study: ListedStudy }) {
  const horizonStart = new Date(study.dt_inicio * 1000).toLocaleDateString('pt-BR');
  const horizonEnd = new Date(study.dt_fim * 1000).toLocaleDateString('pt-BR');
  const lastExecution = study.dt_last_execution === null ? 'Não executado' : new Date(study.dt_last_execution * 1000).toLocaleString('pt-BR');
  const lastResponse = study.dt_last_response === null ? 'Não houve resposta' : new Date(study.dt_last_response * 1000).toLocaleString('pt-BR');
  const { data: userData, error: userError } = useGetUserQuery({ id: study.id_user ?? 0 });
  const {
    data: companyData, error: companyError,
  } = useGetCompanyQuery({ companyId: study.id_empresa ?? 0 });
  const {
    data: downloadLink,
    error: downloadLinkError,
    isLoading: downloadLinkLoading,
  } = useGetDownloadLinkQuery({ id: study.id });
  const [triggerReexecute] = useReexecuteMutation();
  const userId = useSelector(selectUserId);
  const isAdmin = useSelector(selectUserIsAdmin);
  // O backend decide a posse; aqui só não oferecemos a ação a quem não é o dono
  const isOwner = isAdmin || (study.id_user !== null && study.id_user === userId);
  const running = isStudyRunning(study);
  const {
    confirmationDialogBox, feedback, clearFeedback,
    confirmDelete, confirmCancel, deleting, cancelling,
  } = useStudyRowActions(study);
  useHandleRequestErrors(userError as ResponseError | undefined);
  useHandleRequestErrors(companyError as ResponseError | undefined);
  useHandleRequestErrors(downloadLinkError as ResponseError | undefined);

  return (
    <div className="list-study-row my-3 px-4">
      <Row>
        <Col md={4}>
          <Stack>
            <div className="study-name">
              { study.ds_nome_estudo }
            </div>
            <div className="study-description">
              { companyData?.razao_social }
            </div>
            <div className="study-description">
              { study.cenarios[0].blocos[0].ds_modelo.split('/').at(-1) }
            </div>
            <div className="study-description">
              { userData?.first_name }
              {' '}
              { userData?.last_name }
            </div>
          </Stack>
        </Col>
        <Col md={4}>
          <div className="horizon">
            { horizonStart }
            {' -- '}
            { horizonEnd }
          </div>
        </Col>
        <Col md={4}>
          <Stack className="study-status">
            <StatusTag study={study} />
            <div>
              Última Execução:
              {' '}
              { lastExecution }
            </div>
            <div>
              Última Resposta:
              {' '}
              { lastResponse }
            </div>
            <div>
              Número de Execuções:
              {' '}
              { study.vl_execution_counter }
            </div>
            {/* Flex, não <Col md={1}>: a coluna, mais estreita que o botão, cobria o anterior */}
            <div className="study-row-actions d-flex flex-row flex-wrap align-items-center gap-2 mt-1">
              <Link to={downloadLink || ''}>
                <ActionButton
                  icon="download"
                  loading={downloadLinkLoading}
                  onClick={() => {}}
                  tooltipText="Baixe o arquivo de estudo"
                  id={`download-action-${study.id}`}
                />
              </Link>
              <ActionButton
                icon="send"
                onClick={() => {
                  triggerReexecute(study.id);
                }}
                tooltipText="Execute o estudo novamente"
                id={`reruns-study-${study.id}`}
              />
              {isOwner && !isStudyFinished(study) && (
                <ActionButton
                  icon="close"
                  loading={cancelling}
                  onClick={confirmCancel}
                  tooltipText="Cancele a execução do estudo"
                  id={`cancel-study-${study.id}`}
                />
              )}
              {isOwner && (
                <ActionButton
                  icon="trash"
                  loading={deleting}
                  disabled={running}
                  onClick={confirmDelete}
                  tooltipText={running ? 'Cancele o estudo antes de removê-lo' : 'Remova o estudo'}
                  id={`delete-study-${study.id}`}
                />
              )}
            </div>
            {feedback && (
              <Snackbar
                className="mt-2"
                variant={feedback.variant}
                closeButton
                onClose={clearFeedback}
              >
                {feedback.message}
              </Snackbar>
            )}
          </Stack>
        </Col>
      </Row>
      {confirmationDialogBox}
    </div>
  );
}
